import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, MapPin, CalendarDays } from "lucide-react";
import SpaceManager from "@/pages/SpaceManager";
import FacilitySchedule from "@/pages/FacilitySchedule";
import CrossFacilityCalendar from "@/pages/CrossFacilityCalendar";
import { Layers } from "lucide-react";

const TABS = [
  { id: "schedule", label: "Room Schedule", icon: CalendarDays,
    hint: "Cross-production calendar — who is in every room this week." },
  { id: "cross", label: "Cross-facility", icon: Layers,
    hint: "One calendar, every facility, colour-coded by show — double-books surface instantly." },
  { id: "spaces", label: "Rooms & Facilities", icon: Building2,
    hint: "Buildings and rooms available for rehearsal.", adminOnly: true },
];

/**
 * Facilities hub — mirrors the Rehearsals / Notes hub pattern. Any admin/
 * staff sees the cross-production schedule; only admins can manage the
 * building/room list. Cast never lands here — the route is gated in App.js.
 */
export default function FacilityHome() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "schedule";

  const visibleTabs = useMemo(() => TABS.filter((t) => {
    if (t.adminOnly && !(user.role === "org_admin" || user.role === "super_admin")) return false;
    return true;
  }), [user.role]);

  const validInitial = visibleTabs.find((t) => t.id === initial)?.id || visibleTabs[0]?.id;
  const [tab, setTab] = useState(validInitial);

  const setActive = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  const active = visibleTabs.find((t) => t.id === tab) || visibleTabs[0];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="facilities-hub">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Venue</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Facilities</h1>
        <p className="mt-1 text-zinc-400 text-sm">{active?.hint}</p>
      </header>

      <div className="flex flex-wrap gap-1 border border-white/10 rounded-md bg-[#121212] p-1 w-fit" data-testid="facilities-tabs">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`facilities-tab-${id}`}
            onClick={() => setActive(id)}
            className={`h-9 px-3 rounded text-sm inline-flex items-center gap-1.5 transition-colors
              ${tab === id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {tab === "schedule" && <FacilitySchedule embedded />}
        {tab === "cross" && <CrossFacilityCalendar embedded />}
        {tab === "spaces" && <SpaceManager embedded />}
      </div>
    </div>
  );
}
